import omit from 'lodash/omit';
import type { ApolloError } from 'apollo-client';
import type {
	UiModificationsExtension,
	CustomFieldExtension,
} from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import type {
	Project,
	SelectedProject,
	FormError,
	IssueOrRequestType,
	IssueType,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import type { User } from '@atlassian/jira-shared-types/src/rest/jira/user.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createHook,
	type Action,
	createActionsHook,
} from '@atlassian/react-sweet-state';
import type {
	MetaData,
	InlineCreateError,
	ResetForm,
	CreatedIssue,
	InteractedFields,
} from './types.tsx';

export type State = {
	selectedProject: SelectedProject;
	selectedIssueType: IssueOrRequestType;
	requestBackedIssueType: IssueType | null;
	isEpicLinkSelected: boolean;
	isSubmitting: boolean;
	isOperationInProgress: boolean;
	shouldShowConfigureFieldModal: boolean;
	shouldShowFindFieldModal: boolean;
	meta: MetaData;
	dataFetchError: ApolloError | null;
	isFormDirty: boolean;
	areConfiguredFieldsModified: boolean;
	timeTrackingValue: string;
	ecosystem: {
		forge: {
			customFields: CustomFieldExtension[];
			issueAdjustmentModules: UiModificationsExtension[];
			filteredOutModules: CustomFieldExtension[];
			hasFinishedLoading: boolean;
		};
	};
	// active tab index
	currentActiveTabIndex: number;
	shouldAutoFocus: boolean;
	resetFormAction: ResetForm | null;
	isForgeFieldsDetected: boolean;
	canCreateAnotherIssue: boolean;
	createdIssues: CreatedIssue[];
	watchers: User[];
	interactedFields: InteractedFields;
	/* Index of tab with collapsed area expanded */
	expandedCollapsedAreas: number[];
	/* Whether or not to show the `NEW` lozenges next to the collapsed area */
	isSmartFieldsNew?: boolean;
	/* Used to determine if the "Hide unused fields" i.e. smart fields toggle as been interacted to reload the form */
	isSmartFieldsToggled?: boolean;
	/* Whether or not the form is loaded. Set to true when the first field is rendered */
	isFormLoaded: boolean;
};

export type FormState = {
	selectedProject: SelectedProject;
	selectedIssueType: IssueOrRequestType;
	requestBackedIssueType: IssueType | null;
	meta: MetaData;
	areConfiguredFieldsModified: boolean;
};

export const initialState: State = {
	selectedProject: null,
	selectedIssueType: null,
	requestBackedIssueType: null,
	isEpicLinkSelected: false,
	isSubmitting: false,
	isOperationInProgress: false,
	shouldShowConfigureFieldModal: false,
	shouldShowFindFieldModal: false,
	meta: {
		areRequiredFieldsSupported: true,
		queryErrorMessage: '',
		submitErrorMessage: '',
		submitFieldwiseErrors: {},
		inlineCreateError: null,
	},
	dataFetchError: null,
	isFormDirty: false,
	areConfiguredFieldsModified: false,
	timeTrackingValue: '',
	ecosystem: {
		forge: {
			customFields: [],
			filteredOutModules: [],
			issueAdjustmentModules: [],
			hasFinishedLoading: false,
		},
	},
	currentActiveTabIndex: 0,
	shouldAutoFocus: true,
	resetFormAction: null,
	isForgeFieldsDetected: false,
	canCreateAnotherIssue: false,
	createdIssues: [],
	watchers: [],
	interactedFields: {},
	expandedCollapsedAreas: [],
	isSmartFieldsNew: undefined,
	isSmartFieldsToggled: false,
	isFormLoaded: false,
};

export const actions = {
	restoreState:
		(state: Partial<State>): Action<State> =>
		({ setState }) => {
			setState(omit(state, ['isSubmitting', 'isOperationInProgress']));
		},
	setCurrentActiveTabIndex:
		(tabIndex: number): Action<State> =>
		({ setState, getState }) => {
			const { currentActiveTabIndex } = getState();
			if (tabIndex >= 0 && currentActiveTabIndex !== tabIndex) {
				setState({
					currentActiveTabIndex: tabIndex,
				});
			}
		},
	// To enable/disable autofocus of the "create issue" form element - https://a11y.atlassian.net/browse/JCA11Y-318
	setShouldAutoFocus:
		(shouldAutoFocus: boolean): Action<State> =>
		({ setState }) => {
			setState({
				shouldAutoFocus,
			});
		},
	setSelectedProject:
		(project: Project): Action<State> =>
		({ setState }) => {
			setState({
				selectedProject: project,
			});
		},
	setSelectedIssueType:
		(issueType: IssueOrRequestType): Action<State> =>
		({ setState }) => {
			setState({
				selectedIssueType: issueType,
			});
		},
	setRequestBackedIssueType:
		(issueType: IssueType | null): Action<State> =>
		({ setState }) => {
			setState({
				requestBackedIssueType: issueType,
			});
		},
	setIsEpicLinkSelected:
		(isEpicLinkSelected: boolean): Action<State> =>
		({ setState }) => {
			setState({
				isEpicLinkSelected,
			});
		},
	setMeta:
		(meta: MetaData): Action<State> =>
		({ setState }) => {
			setState({
				meta,
			});
		},
	setSubmitErrorMessage:
		(submitErrorMessage?: string): Action<State> =>
		({ setState, getState }) => {
			const { meta } = getState();
			setState({
				meta: {
					...meta,
					submitErrorMessage,
				},
			});
		},
	setTimeTrackingValue:
		(timeTrackingValue: string): Action<State> =>
		({ setState }) => {
			setState({
				timeTrackingValue,
			});
		},
	setSubmitFieldwiseErrors:
		(fieldwiseErrors: FormError): Action<State> =>
		({ setState, getState }) => {
			const { meta } = getState();
			setState({
				meta: {
					...meta,
					submitFieldwiseErrors: fieldwiseErrors,
				},
			});
		},
	setInlineErrorMessage:
		(inlineCreateError: InlineCreateError): Action<State> =>
		({ setState, getState }) => {
			const { meta } = getState();
			setState({
				meta: {
					...meta,
					inlineCreateError,
				},
			});
		},
	setSubmitting:
		(isSubmitting: boolean): Action<State> =>
		({ setState }) => {
			setState({
				isSubmitting,
			});
		},
	setIsOperationInProgress:
		(isOperationInProgress: boolean): Action<State> =>
		({ setState }) => {
			setState({
				isOperationInProgress,
			});
		},
	toggleConfigureFieldsModalVisibility:
		(shouldShowConfigureFieldModal: boolean): Action<State> =>
		({ setState }) => {
			setState({
				shouldShowConfigureFieldModal,
			});
		},
	toggleFindFieldsModalVisibility:
		(shouldShowFindFieldModal: boolean): Action<State> =>
		({ setState }) => {
			setState({
				shouldShowFindFieldModal,
			});
		},
	resetFormContext:
		(): Action<State> =>
		({ setState }) => {
			setState({
				...initialState,
			});
		},
	setDataFetchError:
		(error: ApolloError | null): Action<State> =>
		({ setState }) => {
			setState({
				dataFetchError: error,
			});
		},
	setIsFormDirty:
		(isFormDirty: boolean): Action<State> =>
		({ setState }) => {
			setState({
				isFormDirty,
			});
		},
	setAreConfiguredFieldsModified:
		(areConfiguredFieldsModified: boolean): Action<State> =>
		({ setState }) => {
			setState({
				areConfiguredFieldsModified,
			});
		},
	setForgeModules:
		(
			customFields: CustomFieldExtension[],
			issueAdjustmentModules: UiModificationsExtension[],
			filteredOutModules: CustomFieldExtension[],
			hasFinishedLoading: boolean,
		): Action<State> =>
		({ setState, getState }) => {
			const { ecosystem } = getState();
			setState({
				ecosystem: {
					...ecosystem,
					forge: {
						customFields,
						issueAdjustmentModules,
						hasFinishedLoading: !!hasFinishedLoading,
						filteredOutModules,
					},
				},
			});
		},
	setResetFormAction:
		(resetFormAction: ResetForm | null): Action<State> =>
		({ setState }) => {
			setState({
				resetFormAction,
			});
		},
	setIsForgeFieldsDetected:
		(isForgeFieldsDetected: boolean): Action<State> =>
		({ setState }) => {
			setState({
				isForgeFieldsDetected,
			});
		},
	setCanCreateAnotherIssue:
		(canCreateAnotherIssue: boolean): Action<State> =>
		({ setState }) => {
			setState({
				canCreateAnotherIssue,
			});
		},
	addCreatedIssue:
		(createdIssue: CreatedIssue): Action<State, void, CreatedIssue[]> =>
		({ getState, setState }) => {
			const { createdIssues } = getState();

			const mergedCreatedIssues = [...createdIssues, createdIssue];

			setState({
				createdIssues: mergedCreatedIssues,
			});

			return mergedCreatedIssues;
		},
	setWatchers:
		(watchers: User[]): Action<State> =>
		({ setState }) => {
			setState({
				watchers,
			});
		},
	setInteractedWithField:
		(fieldId: string): Action<State> =>
		({ setState, getState }) => {
			const { interactedFields } = getState();
			setState({
				interactedFields: {
					...interactedFields,
					[fieldId]: true,
				},
			});
		},
	setExpandedCollapsedArea:
		(index: number, shouldExpand: boolean): Action<State> =>
		({ setState, getState }) => {
			const { expandedCollapsedAreas } = getState();
			const newExpandedCollapsedAreas = expandedCollapsedAreas.slice(0);
			const isCurrentExpanded = expandedCollapsedAreas.includes(index);
			let shouldUpdate = false;

			if (!isCurrentExpanded && shouldExpand) {
				newExpandedCollapsedAreas.push(index);
				shouldUpdate = true;
			}

			if (isCurrentExpanded && !shouldExpand) {
				newExpandedCollapsedAreas.splice(newExpandedCollapsedAreas.indexOf(index), 1);
				shouldUpdate = true;
			}

			if (shouldUpdate) {
				setState({
					expandedCollapsedAreas: newExpandedCollapsedAreas,
				});
			}
		},
	resetExpandedCollapsedArea:
		(): Action<State> =>
		({ setState }) => {
			setState({
				expandedCollapsedAreas: [],
			});
		},
	setIsSmartFieldsNew:
		(isSmartFieldsNew: boolean): Action<State> =>
		({ setState }) => {
			setState({
				isSmartFieldsNew,
			});
		},
	setIsSmartFieldsToggled:
		(isSmartFieldsToggled: boolean): Action<State> =>
		({ setState }) => {
			setState({
				isSmartFieldsToggled,
			});
		},
	setIsFormLoaded:
		(isFormLoaded: boolean): Action<State> =>
		({ setState }) => {
			setState({
				isFormLoaded,
			});
		},
} as const;

export type Actions = typeof actions;

const store = createStore<State, Actions>({
	name: 'issue-create.issue-create-form.form-context',
	initialState,
	actions,
});

export const getCreateFormStateSelector = (state: State) => {
	const {
		selectedProject,
		selectedIssueType,
		meta,
		areConfiguredFieldsModified,
		requestBackedIssueType,
		isFormDirty,
	} = state;

	return {
		selectedProject,
		selectedIssueType,
		requestBackedIssueType,
		meta,
		areConfiguredFieldsModified,
		isFormDirty,
	};
};

export const useCreateFormState = createHook(store, {
	selector: getCreateFormStateSelector,
});

export const useFormContext = createHook(store);
export const useFormContextActions = createActionsHook(store);

const getInteractedFieldsSelector = (state: State) => state.interactedFields;
export const useFormContextInteractedFields = createHook(store, {
	selector: getInteractedFieldsSelector,
});

const getIsCollapsedAreaExpandedSelector = (state: State, index: number) =>
	state.expandedCollapsedAreas.includes(index);
export const useIsCollapsedAreaExpanded = createHook<State, Actions, boolean, number>(store, {
	selector: (state, index) => getIsCollapsedAreaExpandedSelector(state, index),
});

export const useIsSmartFieldsNew = createHook(store, {
	selector: (state) => state.isSmartFieldsNew,
});
